/* Reasons — a list you hover on desktop, a swipeable carousel on mobile.

   Desktop: pointing at a title reveals its card. No click needed, but clicking
   and keyboard focus work too.
   Mobile: the cards become one horizontal scroller with arrows and dots. */

class ReasonTabs extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-reason-item]'));
    this.cards = Array.from(this.querySelectorAll('[data-reason-card]'));
    this.dots = Array.from(this.querySelectorAll('[data-reason-dot]'));
    this.track = this.querySelector('[data-reason-track]');
    this.prev = this.querySelector('[data-reason-prev]');
    this.next = this.querySelector('[data-reason-next]');

    if (!this.cards.length) return;

    this.desktop = window.matchMedia('(min-width: 750px)');
    this.index = 0;

    this.items.forEach((item, i) => {
      // Hover is the primary interaction on desktop.
      item.addEventListener('mouseenter', () => {
        if (this.desktop.matches) this.select(i);
      });
      item.addEventListener('focus', () => {
        if (this.desktop.matches) this.select(i);
      });
      item.addEventListener('click', () => this.goTo(i));
    });

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.index - 1));
    if (this.next) this.next.addEventListener('click', () => this.goTo(this.index + 1));

    if (this.track) {
      let frame;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.syncFromScroll());
      }, { passive: true });
    }

    this.desktop.addEventListener('change', () => this.select(this.index));

    this.select(0);
  }

  select(index) {
    if (index < 0) index = 0;
    if (index > this.cards.length - 1) index = this.cards.length - 1;
    this.index = index;

    this.items.forEach((item, i) => {
      const active = i === index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
      item.tabIndex = active ? 0 : -1;
    });

    this.cards.forEach((card, i) => {
      card.classList.toggle('is-active', i === index);
    });

    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (this.prev) this.prev.disabled = index === 0;
    if (this.next) this.next.disabled = index === this.cards.length - 1;
  }

  goTo(index) {
    if (index < 0 || index > this.cards.length - 1) return;
    this.select(index);

    // On mobile the cards are a scroller, so move it to the chosen card.
    if (!this.desktop.matches && this.track) {
      const card = this.cards[index];
      this.track.scrollTo({ left: card.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }
  }

  syncFromScroll() {
    if (this.desktop.matches || !this.track) return;

    const middle = this.track.scrollLeft + this.track.clientWidth / 2;
    let closest = 0;
    let best = Infinity;

    this.cards.forEach((card, i) => {
      const centre = card.offsetLeft - this.track.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(centre - middle);
      if (distance < best) {
        best = distance;
        closest = i;
      }
    });

    if (closest !== this.index) this.select(closest);
  }
}

if (!customElements.get('reason-tabs')) {
  customElements.define('reason-tabs', ReasonTabs);
}
